import React from "react";
import { Budget, PremisesData } from "../../types/budgeting";
import StatusBadge from "./StatusBadge";
import { createFullAddress } from "../../utils/budgetingUtils";

interface PremisesInformationProps {
  budget: Budget;
  setBudget: React.Dispatch<React.SetStateAction<Budget>>;
  premisesData: PremisesData[];
  isViewMode: boolean;
  isUpdateMode: boolean;
  handlePremisesChange: (premisesId: string) => void;
  mode: string;
  validationErrors?: { [key: string]: boolean };
}

const PremisesInformation: React.FC<PremisesInformationProps> = ({
  budget,
  setBudget,
  premisesData,
  isViewMode,
  isUpdateMode,
  handlePremisesChange,
  mode,
  validationErrors = {},
}) => {
  const selectedPremisesName =
    premisesData.find((premise) => premise.id === budget.premises)
      ?.premisesName || "";

  return (
    <div className="bg-[#F8F8FA] px-2 py-4 ">
      {/* <h2 className="font-semibold text-gray-700 mb-4">Premises Information</h2> */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Premises*
          </label>
          <div className="relative">
            <select
              id="premises"
              className={`w-full border ${
                validationErrors.premises ? "border-red-500" : "border-gray-300"
              } rounded-md p-2 pr-8 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
                isViewMode ? "bg-gray-100" : ""
              }`}
              value={budget.premises || ""}
              onChange={(e) => handlePremisesChange(e.target.value)}
              disabled={isViewMode}
            >
              <option value="">Select Premises</option>
              {premisesData.map((premise: PremisesData) => (
                <option key={premise.id} value={premise.id}>
                  {premise.premisesName}
                </option>
              ))}
            </select>
            {validationErrors.premises && !isViewMode && (
              <p className="text-red-500 text-xs mt-1">Premises is required</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address of premises*
          </label>
          <input
            id="premisesAddress"
            type="text"
            className={`w-full border ${
              validationErrors.premisesAddress
                ? "border-red-500"
                : "border-gray-300"
            } rounded-md p-2 bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm`}
            value={budget.premisesAddress}
            disabled
          />
          {validationErrors.premisesAddress && !isViewMode && (
            <p className="text-red-500 text-xs mt-1">Address is required</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Requested By
          </label>
          <input
            type="text"
            className={`w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
              isViewMode ? "bg-gray-100" : ""
            }`}
            value={budget.requestedBy || ""}
            onChange={(e) =>
              !isViewMode &&
              setBudget((prev) => ({ ...prev, requestedBy: e.target.value }))
            }
            disabled={isViewMode}
            placeholder="Enter name of requester"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Approval Manager
          </label>
          <input
            type="text"
            className={`w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
              isViewMode ? "bg-gray-100" : ""
            }`}
            value={budget.approvalManager || ""}
            onChange={(e) =>
              !isViewMode &&
              setBudget((prev) => ({
                ...prev,
                approvalManager: e.target.value,
              }))
            }
            disabled={isViewMode}
            placeholder="Enter name of approval manager"
          />
        </div>

        {!mode.startsWith("create") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            {isViewMode ? (
              <div className="p-2">
                <StatusBadge status={budget.status} />
              </div>
            ) : (
              isUpdateMode && (
                <div className="flex items-center gap-4">
                  <select
                    className="w-full border border-gray-300 rounded-md p-2 pr-8 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                    value={budget.status || "Pending"}
                    onChange={(e) =>
                      setBudget((prev) => ({ ...prev, status: e.target.value }))
                    }
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                    <option value="In Review">In Review</option>
                    <option value="Waiting For Approvals">
                      Waiting For Approvals
                    </option>
                    <option value="Expired">Expired</option>
                    <option value="Partially Approved">
                      Partially Approved
                    </option>
                  </select>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PremisesInformation;
